import { t as localeText } from "@/i18n/translate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { setSpaceIDGetter } from "@/api/client";
import { queryKeys } from "@/api/query-keys";
import { ActiveSpaceProvider } from "@/shared/lib/activeSpace";
import { useOnlineStatus } from "@/shared/lib/hooks/useOnlineStatus";
import { Button, Spinner, Surface, Text } from "@/shared/ui";
import { listSpaces } from "../api/foundation";
import { SpaceContext, type SpaceContextValue } from "./spaceContext";

const STORAGE_KEY = "anyai-v2-space-id";

function storedSpaceID(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function SpaceProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const selectedRef = useRef<string | null>(null);
  const errorHeadingRef = useRef<HTMLHeadingElement>(null);
  const [selectedID, setSelectedID] = useState<string | null>(null);
  const online = useOnlineStatus();
  const spacesQuery = useQuery({
    queryKey: queryKeys.foundation.spaces(),
    queryFn: listSpaces,
    staleTime: 60_000,
  });

  useEffect(() => {
    setSpaceIDGetter(() => selectedRef.current);
    return () => setSpaceIDGetter(() => null);
  }, []);

  useEffect(() => {
    if (!spacesQuery.data || selectedID) return;
    const stored = storedSpaceID();
    const initial =
      spacesQuery.data.find((space) => space.id === stored) ??
      spacesQuery.data.find((space) => space.personal_default) ??
      spacesQuery.data[0];
    if (!initial) return;
    selectedRef.current = initial.id;
    setSelectedID(initial.id);
  }, [selectedID, spacesQuery.data]);

  const selectSpace = useCallback(
    (id: string) => {
      if (!id || id === selectedRef.current) return;
      selectedRef.current = id;
      setSelectedID(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // A non-persisted selection remains valid for this session.
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.foundation.all });
    },
    [queryClient],
  );

  const selectedSpace = spacesQuery.data?.find((space) => space.id === selectedID);
  const value = useMemo<SpaceContextValue | null>(
    () =>
      selectedSpace
        ? { spaces: spacesQuery.data ?? [], selectedSpace, selectSpace }
        : null,
    [selectSpace, selectedSpace, spacesQuery.data],
  );

  const coldOffline = !online && !spacesQuery.data;
  const unavailable = coldOffline || spacesQuery.isError || (spacesQuery.isSuccess && !value);
  const unavailableTitle = coldOffline || spacesQuery.isError
    ? t("projectShell.workspaceLoadErrorTitle")
    : t("projectShell.workspaceEmptyTitle");

  useEffect(() => {
    if (!unavailable) return;
    document.title = `${unavailableTitle} · Afterform`;
    const frame = requestAnimationFrame(() => errorHeadingRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [unavailable, unavailableTitle]);

  if (unavailable) {
    return (
      <main
        id="main-content"
        data-system-state={coldOffline ? "offline" : spacesQuery.isError ? "error" : "empty"}
        className="grid min-h-screen min-h-dvh place-items-center bg-canvas px-4 py-10"
      >
        <Surface
          as="section"
          aria-labelledby="workspace-load-error"
          tone="paper"
          border="hairline"
          radius="lg"
          className="w-full max-w-[680px] p-6 sm:p-8"
        >
          <h1
            id="workspace-load-error"
            ref={errorHeadingRef}
            tabIndex={-1}
            aria-describedby="workspace-load-description"
            className="scroll-mt-20 font-sans text-2xl font-semibold text-ink outline-none"
          >
            {unavailableTitle}
          </h1>
          <Text
            id="workspace-load-description"
            variant="body"
            className="mt-2 max-w-[65ch] text-pretty"
          >
            {!online
              ? t("projectShell.workspaceOfflineDescription")
              : spacesQuery.isError
                ? t("projectShell.workspaceLoadErrorDescription")
                : t("projectShell.workspaceEmptyDescription")}
          </Text>
          <Button
            className="mt-6"
            variant="ink"
            disabled={spacesQuery.isFetching}
            onClick={() => void spacesQuery.refetch()}
          >
            {spacesQuery.isFetching ? <Spinner label={t("common.loading")} /> : null}
            {t("errors.tryAgain")}
          </Button>
        </Surface>
      </main>
    );
  }

  if (!value) {
    return (
      <main
        id="main-content"
        aria-busy="true"
        data-system-state="loading"
        className="grid min-h-screen min-h-dvh place-items-center bg-canvas"
      >
        <div>
          <Spinner label={t("projectShell.workspaceLoading")} />
        </div>
      </main>
    );
  }

  return (
    <SpaceContext.Provider value={value}>
      <ActiveSpaceProvider value={value.selectedSpace}>{children}</ActiveSpaceProvider>
    </SpaceContext.Provider>
  );
}

export function useSpace(): SpaceContextValue {
  const value = useContext(SpaceContext);
  if (!value) throw new Error(localeText("interface.usespace_must_be_used_inside_spaceprovider_cf482626", { defaultValue: "useSpace must be used inside SpaceProvider" }));
  return value;
}
